/* eslint-disable react/jsx-props-no-spreading */
import React from 'react';
import {
  Alert,
  AlertTitle,
  Autocomplete,
  Box,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { ComboBoxOption, RichTextEditor } from '@marageti/z4-lib';
import { ApiError } from '@marageti/z4-sdk/lib/tracking';
import MatchSuggestionCard from '../../../components/trip-request-matching/match-suggestion-card/match-suggestion-card';
import { MatchSelection } from '../../../store/contexts/matching/types';

type Props = {
  additionalComments: string;
  cooperativeMatch: boolean;
  onChangeSpecialNotes: (e: React.SyntheticEvent, v: ComboBoxOption[]) => void;
  partnerNotRecommended: boolean;
  selectedMatches: { [key: string]: MatchSelection };
  selectedSpecialNotes: ComboBoxOption[];
  setAdditionalComments: (aC: string) => void;
  specialNotesOptions: ComboBoxOption[];
  submissionError?: ApiError;
};

const MatchConfirmationView = ({
  additionalComments,
  cooperativeMatch,
  onChangeSpecialNotes,
  partnerNotRecommended,
  selectedMatches,
  selectedSpecialNotes,
  setAdditionalComments,
  specialNotesOptions,
  submissionError = undefined,
}: Props) => (
  <Box>
    <Typography variant="h3" mb={2}>
      Confirm Match
    </Typography>
    {partnerNotRecommended && (
      <Alert severity="warning" sx={{ mb: 4 }}>
        <AlertTitle>Selected Partner is not recommended</AlertTitle>
        You&apos;ve selected a partner that is not recommended for this trip. You can still proceed, but please confirm your selections.
      </Alert>
    )}
    {cooperativeMatch && (
      <Alert severity="info" sx={{ mb: 1.5 }}>
        <AlertTitle>Cooperative Match</AlertTitle>
        The traveler will work with multiple partners to plan different segments of this trip.
      </Alert>
    )}
    {submissionError && (
      <Alert severity="error" sx={{ mb: 1.5 }}>
        <AlertTitle>There was an error matching trip</AlertTitle>
        {submissionError.message}
        {submissionError.code && `Code: ${submissionError.code} `}
        {submissionError.id && `ID: ${submissionError.id}`}
      </Alert>
    )}
    <Stack spacing={1.5}>
      {Object.entries(selectedMatches).map(([key, matchSelection]) => (
        <MatchSuggestionCard
          disabled
          displayCountries={cooperativeMatch}
          forCountries={matchSelection.forCountries}
          key={key}
          matchSuggestion={matchSelection.match}
        />
      ))}
    </Stack>
    <Autocomplete
      autoComplete
      filterSelectedOptions
      id="special-notes"
      isOptionEqualToValue={(option, value) => (option.label === value.label)}
      multiple
      onChange={onChangeSpecialNotes}
      options={specialNotesOptions}
      renderInput={(params) => (
        <TextField
          {...params}
          label="Special Notes"
          fullWidth
        />
      )}
      value={selectedSpecialNotes}
    />
    <RichTextEditor
      editorName="Additional-Comments-Editor"
      label="Additional Comments"
      defaultValue={additionalComments}
      onChange={(value) => {
        setAdditionalComments(value);
      }}
      useLinks
      useLists
      sx={{
        minHeight: '300px',
        maxHeight: '300px',
        flex: 1,
      }}
    />
  </Box>
);

export default MatchConfirmationView;
